import type { Finding, FindingStatement } from '@/models/report';

export const EMPTY_STATEMENTS = '[]';

export const newStatement = (value: string): FindingStatement => ({
  uuid: crypto.randomUUID(),
  value,
});

/** Live rows per class in index order. */
export function groupFindingStatements(
  findings: Finding[]
): Record<string, FindingStatement[]> {
  const out: Record<string, FindingStatement[]> = {};
  [...findings]
    .filter((f) => !f.isDeleted)
    .sort((a, b) => a.index - b.index)
    .forEach((f) => {
      (out[f.findingClass] ??= []).push({
        uuid: f.uuid,
        value: f.value,
        valueSource: f.valueSource ?? null,
      });
    });
  return out;
}

export function statementValues(rows: FindingStatement[]): string[] {
  return rows.map((r) => r.value);
}

/** Value-only fingerprint: uuids are identity, not content. */
export function statementSignature(rows: FindingStatement[]): string {
  return JSON.stringify(statementValues(rows));
}

export function joinFindingValues(findings: Finding[]): Map<string, string> {
  return new Map(
    Object.entries(groupFindingStatements(findings)).map(([fc, rows]) => [
      fc,
      statementValues(rows).join(' '),
    ])
  );
}

/** Texts back onto rows: unchanged texts keep their row, new ones get a fresh uuid. */
export function reconcileStatements(
  current: FindingStatement[],
  texts: string[]
): FindingStatement[] {
  const pool = [...current];
  return texts.map((value) => {
    const i = pool.findIndex((r) => r.value === value);
    return i === -1 ? newStatement(value) : pool.splice(i, 1)[0];
  });
}
